import { useTranslation } from 'react-i18next';

import { IndicatorName, PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { useFact } from '@/hooks/useFact';
import { useOrganizationIndicatorReportingFact } from '@/pages/shared/hooks/useOrganizationFact';
import { isNullish } from '@/utils/booleans';
import { formatFactKilogramsToTonnes } from '@/utils/formatting';

import { CapsuleTrend } from './Capsule/Capsule';
import { printLatestDate, printNthYearDate, useNCCardContext } from './NCCard/NCCard';
import {
  getNCChangeIcon,
  getNCChangeIconVariant,
  NCFactCapsule,
  NCFactCapsuleProps,
} from './NCFactCapsule/NCFactCapsule';

type LatestAnalysisFactProps = {
  latestAnalysis: PlotReportFactElement<number | null> | undefined;
  oneYearTrend: PlotReportFactElement<number | null> | undefined;
};

export const LatestAnalysisFact = ({ latestAnalysis, oneYearTrend }: LatestAnalysisFactProps) => {
  const { t } = useTranslation();

  // Explainer populated only for carbon number
  let equivalentExplainer = useCarbonEquivalentsExplainer();
  if (!latestAnalysis?.name?.includes('carbon')) equivalentExplainer = '';

  const formattedLatestAnalysis = formatFactKilogramsToTonnes(latestAnalysis);

  let oneYearTrendDisplay = `${useDisplayNumber(Number(oneYearTrend?.value ?? 0), { signDisplay: 'always' })} %`;
  oneYearTrendDisplay = !isNullish(oneYearTrend?.value) ? oneYearTrendDisplay : '-';

  const latestAnalysisDate = printLatestDate([formattedLatestAnalysis?.measured_at]);

  const latestAnalysisBody = t('shared.ncaDetail.details.explainers.latestAnalysis.body', {
    date: latestAnalysisDate,
  });
  const popoverLatestAnalysis: NCFactCapsuleProps['infoPopoverProps'] = {
    title: t('shared.ncaDetail.details.explainers.latestAnalysis.title'),
    body: equivalentExplainer ? `${latestAnalysisBody}\n${equivalentExplainer}` : `${latestAnalysisBody}`,
    popoverTriggerProps: {
      'data-testid': equivalentExplainer ? 'latest-analysis-popover' : '',
    },
  };

  return (
    <NCFactCapsule
      label={t('shared.ncaDetail.details.labels.latestAnalysis')}
      fact={formattedLatestAnalysis}
      trend={
        typeof oneYearTrend?.value === 'number' && (
          <CapsuleTrend variant={oneYearTrend.value < 0 ? 'negative' : 'positive'}>{oneYearTrendDisplay}</CapsuleTrend>
        )
      }
      capsuleIconProps={{
        icon: getNCChangeIcon(formattedLatestAnalysis),
        variant: getNCChangeIconVariant(formattedLatestAnalysis),
      }}
      infoPopoverProps={popoverLatestAnalysis}
    />
  );
};

type OneYearChangeFactProps = {
  latestAnalysis: PlotReportFactElement<number | null> | undefined;
  oneYearChange: PlotReportFactElement<number | null> | undefined;
};

export const OneYearChangeFact = ({ latestAnalysis, oneYearChange }: OneYearChangeFactProps) => {
  const { t } = useTranslation();

  const formattedLatestAnalysis = formatFactKilogramsToTonnes(latestAnalysis);
  const formattedOneYearChange = formatFactKilogramsToTonnes(oneYearChange);

  const oneYearChangeInitialMeasuredAt = printLatestDate([formattedLatestAnalysis?.measured_at]);
  const oneYearChangePreviousMeasuredAt = printNthYearDate([formattedOneYearChange?.measured_at], -1);

  return (
    <NCFactCapsule
      label={t('shared.ncaDetail.details.labels.oneYearChange')}
      fact={formattedOneYearChange}
      capsuleIconProps={{
        icon: getNCChangeIcon(oneYearChange),
        variant: getNCChangeIconVariant(oneYearChange),
      }}
      infoPopoverProps={{
        title: t('shared.ncaDetail.details.explainers.oneYearChange.title'),
        body: t('shared.ncaDetail.details.explainers.oneYearChange.body', {
          latestAnalysisDate: oneYearChangeInitialMeasuredAt,
          previousAnalysisDate: oneYearChangePreviousMeasuredAt,
        }),
        popoverTriggerProps: {
          'data-testid': '1yr-change-tooltip',
        },
      }}
    />
  );
};

const useCarbonEquivalentsExplainer = () => {
  const { t } = useTranslation();

  const { analysisType } = useNCCardContext();

  const carbonStorageFact =
    analysisType === 'total' ? IndicatorName.permanent_soil_carbon_total : IndicatorName.permanent_soil_carbon_per_ha;
  const carbonStorageBgCo0Fact =
    analysisType === 'total'
      ? IndicatorName.permanent_soil_co2_equivalent_total
      : IndicatorName.permanent_soil_co2_equivalent_per_ha;

  const facts = useOrganizationIndicatorReportingFact<number>([carbonStorageFact, carbonStorageBgCo0Fact]);

  const carbonStorageBgFact = carbonStorageFact in facts ? facts[`${carbonStorageFact}`] : undefined;
  const carbonStorageBgCo0EquivalentFact =
    carbonStorageBgCo0Fact in facts ? facts[`${carbonStorageBgCo0Fact}`] : undefined;

  const carbonStorageBg = formatFactKilogramsToTonnes(carbonStorageBgFact);
  const carbonStorageBgCo2Equivalent = formatFactKilogramsToTonnes(carbonStorageBgCo0EquivalentFact);

  const { display: carbonStorageBgDisplay, value: carbonStorageBgValue } = useFact(carbonStorageBg);

  const { display: carbonStorageBgCo2EquivalentDisplay, value: carbonStorageBgCo2EquivalentValue } =
    useFact(carbonStorageBgCo2Equivalent);

  if (!carbonStorageBgValue || !carbonStorageBgCo2EquivalentValue) return undefined;

  return t('shared.ncaDetail.details.carbon.explainers.equivalents', {
    cValue: carbonStorageBgDisplay,
    co2Value: carbonStorageBgCo2EquivalentDisplay,
  });
};
